import DraftsOutlined from '@mui/icons-material/DraftsOutlined'
import MarkEmailUnreadOutlined from '@mui/icons-material/MarkEmailUnreadOutlined'
import { IconButton, ListItem, Tooltip, Typography } from '@mui/material'
import { isNotificationRead } from '@/service-library/hooks/notifications'
import { Notification } from '@/types/notifications'
import {
  formatNotificationTime,
  getNotificationOpenPath,
} from '@/utils/notifications'
import NotificationButton from './NotificationButton'

type NotificationRowProps = {
  notification: Notification
  documentName?: string
  onOpen: () => void
  onSetRead: (read: boolean) => void
}

export default function NotificationRow({
  notification,
  documentName,
  onOpen,
  onSetRead,
}: NotificationRowProps) {
  const isRead = isNotificationRead(notification)
  const readLabel = isRead ? 'Mark as unread' : 'Mark as read'
  const openPath = getNotificationOpenPath(notification)

  return (
    <ListItem
      disablePadding
      divider
      sx={{
        bgcolor: isRead ? undefined : 'action.hover',
        // While the row is hovered or keyboard-focused, the read toggle takes
        // the timestamp's place, so it never takes space from the text.
        // :focus-visible rather than :focus-within, so a mouse click on the
        // toggle doesn't leave it showing after the pointer moves away.
        // While hidden it ignores the pointer, so a click on the timestamp
        // opens the row instead of toggling it.
        '& .notification-read-toggle': { opacity: 0, pointerEvents: 'none' },
        '&:hover, &:has(:focus-visible)': {
          '& .notification-read-toggle': { opacity: 1, pointerEvents: 'auto' },
          '& .notification-time': { visibility: 'hidden' },
        },
      }}
    >
      <NotificationButton
        notification={notification}
        isRead={isRead}
        onClick={() => {
          if (!isRead) onSetRead(true)
          // A notification with no link has nowhere to go. Clicking it only
          // marks it read, and the popover stays open.
          if (openPath) onOpen()
        }}
        documentName={documentName}
        titleEnd={
          <Typography
            className="notification-time"
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {formatNotificationTime(notification.created_at)}
          </Typography>
        }
      />
      {/* A sibling of the link, not inside it, so clicking it doesn't open the row. */}
      <Tooltip title={readLabel}>
        <IconButton
          className="notification-read-toggle"
          aria-label={readLabel}
          size="small"
          onClick={() => onSetRead(!isRead)}
          sx={{
            position: 'absolute',
            top: 6,
            right: 10,
            p: 0.5,
            transition: 'opacity 150ms',
          }}
        >
          {isRead ? (
            <MarkEmailUnreadOutlined fontSize="small" />
          ) : (
            <DraftsOutlined fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </ListItem>
  )
}
